import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listLookup } from '../../api/masterData';
import { listBranches } from '../../api/branches';
import { CreateCampaignPayload } from '../../api/email';

interface CreateCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  errorMessage: string | null;
  onSubmit: (payload: CreateCampaignPayload) => Promise<void>;
}

export const CreateCampaignDialog = ({ open, onClose, errorMessage, onSubmit }: CreateCampaignDialogProps) => {
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [subjectOverride, setSubjectOverride] = useState('');
  const [leadStatusId, setLeadStatusId] = useState<number | ''>('');
  const [leadSourceId, setLeadSourceId] = useState<number | ''>('');
  const [branchId, setBranchId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ['lookup', '/master-data/email-templates'],
    queryFn: () => listLookup('/master-data/email-templates', { activeOnly: true }),
    enabled: open,
  });
  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
    enabled: open,
  });
  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources'),
    enabled: open,
  });
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: () => listBranches(),
    enabled: open,
  });

  const handleSubmit = async () => {
    if (!name.trim() || !templateId) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name,
        templateId,
        subjectOverride: subjectOverride || undefined,
        audienceFilter: {
          ...(leadStatusId ? { leadStatusId } : {}),
          ...(leadSourceId ? { leadSourceId } : {}),
          ...(branchId ? { branchId } : {}),
        },
      });
      setName('');
      setTemplateId('');
      setSubjectOverride('');
      setLeadStatusId('');
      setLeadSourceId('');
      setBranchId('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New email campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <TextField label="Campaign name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            select
            label="Template"
            fullWidth
            value={templateId}
            onChange={(e) => setTemplateId(Number(e.target.value))}
          >
            {templatesQuery.data?.map((t) => (
              <MenuItem key={t.id as number} value={t.id as number}>
                {String(t.name)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Subject override (optional)"
            fullWidth
            value={subjectOverride}
            onChange={(e) => setSubjectOverride(e.target.value)}
          />

          <Typography variant="subtitle2">Audience filter (leave blank to match all leads with an email)</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Status"
              fullWidth
              value={leadStatusId}
              onChange={(e) => setLeadStatusId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {statusesQuery.data?.map((s) => (
                <MenuItem key={s.id as number} value={s.id as number}>
                  {String(s.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Source"
              fullWidth
              value={leadSourceId}
              onChange={(e) => setLeadSourceId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {sourcesQuery.data?.map((s) => (
                <MenuItem key={s.id as number} value={s.id as number}>
                  {String(s.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Branch"
              fullWidth
              value={branchId}
              onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {branchesQuery.data?.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || !templateId || submitting} onClick={handleSubmit}>
          {submitting ? 'Creating…' : 'Create draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
